/**
 * TROOP-844: Beim Wechsel einer Option (z. B. Farbe) eine gültige Variantenkombination vorwählen.
 *
 * Gibt es die bisher gewählten Folgeoptionen (Taillenweite, Schrittlänge) in der neuen Auswahl nicht,
 * wird je Option der nächstgelegene vorhandene Wert gewählt. So bleibt die Auswahl immer vollständig
 * und die Folgeoptionen verschwinden nicht.
 *
 * Datenquelle: <script data-variant-matrix> in snippets/cheveyo-product-variant-picker.liquid
 */
(function () {
  if (window.__cheveyoVariantPreselect) return;
  window.__cheveyoVariantPreselect = true;

  let pendingNotice = null;

  function readMatrix(variantSelects) {
    const script = variantSelects.querySelector('script[data-variant-matrix]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function toNumber(value) {
    const number = parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(number) ? null : number;
  }

  // Wählt aus den vorhandenen Werten den gewünschten, sonst den numerisch nächstgelegenen.
  function pickValue(candidates, wanted, position) {
    const values = [];
    candidates.forEach((variant) => {
      if (!values.includes(variant.o[position])) values.push(variant.o[position]);
    });
    if (wanted && values.includes(wanted)) return wanted;

    const hasStock = (value) => candidates.some((variant) => variant.o[position] === value && variant.a);
    const pool = values.filter(hasStock).length ? values.filter(hasStock) : values;

    const wantedNumber = toNumber(wanted);
    if (wantedNumber === null) return pool[0];

    return pool.reduce((best, value) => {
      const valueNumber = toNumber(value);
      if (valueNumber === null) return best;
      if (best === null) return value;
      const distance = Math.abs(valueNumber - wantedNumber);
      const bestDistance = Math.abs(toNumber(best) - wantedNumber);
      // Bei gleichem Abstand die kleinere Zahl nehmen, damit das Ergebnis stabil bleibt
      return distance < bestDistance || (distance === bestDistance && valueNumber < toNumber(best)) ? value : best;
    }, null) ?? pool[0];
  }

  function checkedValues(variantSelects) {
    return Array.from(variantSelects.querySelectorAll('fieldset.product-form__input')).map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  function readCurrentOptions(variantSelects) {
    const script = variantSelects.querySelector('script[data-selected-variant]');
    try {
      return JSON.parse(script.textContent).options || [];
    } catch (error) {
      return [];
    }
  }

  function onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    stopNoticeObserver();
    pendingNotice = null;

    const variantSelects = target.closest('variant-selects');
    if (!variantSelects) return;

    const matrix = readMatrix(variantSelects);
    if (!matrix || !matrix.variants.length) return;

    // Liquid liefert höchstens 250 Varianten, bei unvollständiger Matrix greift Dawns Standardverhalten
    if (matrix.complete === false) return;

    // EOL-Produkte zeigen nur verfügbare Werte, nicht verfügbare Varianten gelten daher als nicht vorhanden
    if (matrix.eol) matrix.variants = matrix.variants.filter((variant) => variant.a);
    if (!matrix.variants.length) return;

    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset.product-form__input'));
    // Eine Option ohne Fieldset (z. B. Swatches) würde Position und Werte-IDs gegeneinander verschieben
    if (fieldsets.length !== matrix.ids.length) return;
    const position = fieldsets.indexOf(target.closest('fieldset'));
    if (position < 0 || position === fieldsets.length - 1) return;

    // Fehlt im DOM eine gewählte Option, dient die aktuell gewählte Variante als Ausgangswert
    const currentOptions = readCurrentOptions(variantSelects);
    const selected = checkedValues(variantSelects).map((value, index) => value ?? currentOptions[index] ?? null);
    selected[position] = target.value;

    const exists = matrix.variants.some((variant) => variant.o.every((value, index) => value === selected[index]));
    if (exists) return;

    // Auswahl bis zur geänderten Option fest, die Folgeoptionen werden nachgezogen
    let candidates = matrix.variants.filter((variant) =>
      variant.o.slice(0, position + 1).every((value, index) => value === selected[index])
    );
    if (!candidates.length) return;

    const resolved = selected.slice(0, position + 1);
    let adjusted = false;
    for (let index = position + 1; index < matrix.ids.length; index++) {
      const value = pickValue(candidates, selected[index], index);
      if (value !== selected[index]) adjusted = true;
      resolved.push(value);
      candidates = candidates.filter((variant) => variant.o[index] === value);
    }

    const optionValueIds = resolved.map((value, index) => String(matrix.ids[index][value]));
    if (optionValueIds.some((id) => id === 'undefined')) return;

    // Dawns VariantSelects würde nur die im DOM vorhandenen Werte lesen, daher selbst veröffentlichen
    event.stopImmediatePropagation();
    variantSelects.updateSelectionMetadata?.(event);
    pendingNotice = adjusted ? matrix.notice : null;

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target,
        selectedOptionValues: optionValueIds,
      },
    });
  }

  let noticeObserver = null;

  function renderNotice(text) {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects || variantSelects.querySelector('.cheveyo-variant-notice')) return;

    const notice = document.createElement('p');
    notice.className = 'cheveyo-variant-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = text;
    variantSelects.appendChild(notice);
  }

  function stopNoticeObserver() {
    noticeObserver?.disconnect();
    noticeObserver = null;
  }

  // Dawn ersetzt variant-selects per View Transition mit Verzögerung. Der Hinweis wird deshalb
  // erneut gesetzt, falls er dabei mit dem alten Element verschwindet.
  function showNotice() {
    if (!pendingNotice) return;
    const text = pendingNotice;
    pendingNotice = null;

    stopNoticeObserver();
    renderNotice(text);

    const root = document.querySelector('product-info') || document.body;
    noticeObserver = new MutationObserver(() => renderNotice(text));
    noticeObserver.observe(root, { childList: true, subtree: true });
    setTimeout(stopNoticeObserver, 3000);
  }

  // Capture-Phase am document, damit der Handler vor dem Listener in VariantSelects läuft
  document.addEventListener('change', onChange, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, showNotice);
  }
})();
